import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { LayoutGroup } from 'framer-motion';
import { DragOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { Container } from '@/components/ui/Container';
import ClientTicketCard from '@/components/module/projects/client_tickets_tab/ClientTicketCard';
import { CLIENT_TICKET_COL, getClientTicketApiId } from '@hooks/projects/useProjectClientTickets';
import {
    SprintKanbanScrollChainStyle,
    SprintKanbanDragCursorStyle,
} from '@/components/module/projects/sprints_tab/SprintKanbanBoard';

export const LoadMoreCard = styled.button`
    width: 100%;
    margin-top: 12px;
    padding: 12px;
    border-radius: 8px;
    background: var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.02));
    backdrop-filter: blur(12px);
    border: 1px dashed color-mix(in srgb, var(--ant-color-primary, #7c3aed) 30%, transparent);
    color: var(--ant-color-primary, #7c3aed);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: block;
    text-align: center;
    &:hover {
        background: color-mix(in srgb, var(--ant-color-primary, #7c3aed) 8%, transparent);
        transform: translateY(-1px);
    }
    &:active {
        transform: translateY(0);
    }
`;
import { useSprintKanbanSyncHorizontalScroll } from '@hooks/projects/useProjectSprints';
import { useMatchMedia } from '@hooks/useMatchMedia';
import {
    SprintKanbanFill,
    KanbanThreeColumnRow,
    KanbanColumnPanel,
    KanbanColumnHeader,
    KanbanColumnTitle,
    KanbanColumnCountBadge,
    TaskCardKanbanStatusPillCount,
    KanbanColumnBody,
    KanbanDragDropPlaceholder,
    kanbanDesktopHeaderCellFlexStyle,
    kanbanDesktopBoardRowInnerStyle,
    kanbanDesktopBoardHeaderRowInnerStyle,
} from '@/pages/inside/tenant/style';

const COL_VARIANT = { ACTIVE: 'todo', SUBMITTED: 'bugs', ACKNOWLEDGED: 'completed' };

function EmptyColumn({ title }) {
    return (
        <div
            style={{
                width: '100%',
                minHeight: 120,
                borderRadius: 12,
                border: '1px dashed var(--ant-color-border-secondary, #d9d9d9)',
                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                textAlign: 'center',
                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                fontSize: 13,
            }}
        >
            No items in {title}
        </div>
    );
}

export default function ClientTicketsKanbanBoard({
    activeRows,
    submittedRows,
    acknowledgedRows,
    canDrag,
    canUpdate,
    canAcknowledge,
    dropTarget,
    draggingColumn,
    onDndDragStart,
    onDndDragOverColumn,
    onDndDragEnd,
    onSelectItem,
    hasMore = false,
    onLoadMore = undefined,
}) {
    const isNarrow = useMatchMedia('(max-width: 1024px)');
    const { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll } = useSprintKanbanSyncHorizontalScroll();
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const [activeDragPayload, setActiveDragPayload] = useState(null);

    const handleDragStart = useCallback(
        (event) => {
            const payload = event?.active?.data?.current ?? null;
            setActiveDragPayload(payload);
            onDndDragStart?.(payload);
        },
        [onDndDragStart],
    );

    const canDropToColumn = useCallback(
        (sourceColumn, targetColumn) => {
            if (sourceColumn !== CLIENT_TICKET_COL.SUBMITTED) return false;
            if (targetColumn === CLIENT_TICKET_COL.ACTIVE) return !!canUpdate;
            if (targetColumn === CLIENT_TICKET_COL.ACKNOWLEDGED) return !!canAcknowledge;
            return false;
        },
        [canUpdate, canAcknowledge],
    );

    const handleDragOver = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            const sourceColumn = payload?.sourceColumn ?? null;
            if (!overColumnId || !sourceColumn) return;
            const col = String(overColumnId);
            if (canDropToColumn(sourceColumn, col)) onDndDragOverColumn?.(col, payload);
        },
        [onDndDragOverColumn, canDropToColumn],
    );

    const handleDragEnd = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            const sourceColumn = payload?.sourceColumn ?? null;
            const col = overColumnId != null ? String(overColumnId) : null;
            const canDrop = col && sourceColumn ? canDropToColumn(sourceColumn, col) : false;
            onDndDragEnd?.({ overColumnId: canDrop ? col : null, payload });
            setActiveDragPayload(null);
        },
        [onDndDragEnd, canDropToColumn],
    );

    const handleDragCancel = useCallback(() => setActiveDragPayload(null), []);

    const canDropOnColumn = useCallback(
        (columnId) => {
            if (!canDrag || !draggingColumn || draggingColumn === columnId) return false;
            return canDropToColumn(draggingColumn, columnId);
        },
        [canDrag, draggingColumn, canDropToColumn],
    );

    const showDragPlaceholder = (columnId) => Boolean(canDropOnColumn(columnId));
    const dropZoneActive = (columnId) => Boolean(canDropOnColumn(columnId) && dropTarget === columnId);

    function DroppableKanbanColumnPanel({ columnId, children }) {
        const { setNodeRef } = useDroppable({ id: columnId });
        return <KanbanColumnPanel ref={setNodeRef}>{children}</KanbanColumnPanel>;
    }

    function DraggableTicketCard({ item, sourceColumn, children }) {
        const id = getClientTicketApiId(item);
        const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
            id: `client-ticket-${String(id)}`,
            data: { id, sourceColumn },
            disabled: !canDrag || !id || sourceColumn !== CLIENT_TICKET_COL.SUBMITTED,
        });
        const style = transform
            ? {
                transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            }
            : undefined;
        return (
            <div ref={setNodeRef} style={{ ...style, opacity: isDragging ? 0.55 : 1, width: '100%' }} {...attributes} {...listeners}>
                {children}
            </div>
        );
    }

    const renderCard = (item, sourceColumn, idx) => {
        const id = getClientTicketApiId(item) ?? `${sourceColumn}-${idx}`;
        return (
            <div key={id} style={{ width: '100%' }}>
                <DraggableTicketCard item={item} sourceColumn={sourceColumn}>
                    <ClientTicketCard
                        item={item}
                        displayColumnId={sourceColumn}
                        onSelect={onSelectItem ? () => onSelectItem(item, sourceColumn) : undefined}
                    />
                </DraggableTicketCard>
            </div>
        );
    };

    const renderColumnBody = (columnId, list, title) => (
        <KanbanColumnBody
            className="sprint-kanban-column-body-natural"
            $qaStack
            data-drag-placeholder={showDragPlaceholder(columnId) ? 'true' : undefined}
            $dragPlaceholder={showDragPlaceholder(columnId)}
        >
            {draggingColumn && draggingColumn !== columnId && !canDropOnColumn(columnId) ? (
                <KanbanDragDropPlaceholder role="status" $active={false}>
                    <DragOutlined style={{ fontSize: 24, opacity: 0.5 }} aria-hidden />
                    <span>Only submitted tickets can be moved.</span>
                </KanbanDragDropPlaceholder>
            ) : showDragPlaceholder(columnId) ? (
                <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(columnId)}>
                    <DragOutlined style={{ fontSize: 24, opacity: 0.88 }} aria-hidden />
                    <span>
                        {columnId === CLIENT_TICKET_COL.ACTIVE
                            ? 'Drop here to move to Active'
                            : 'Drop here to move to Acknowledged'}
                    </span>
                </KanbanDragDropPlaceholder>
            ) : (
                <>
                    {list.length === 0 ? (
                        <EmptyColumn title={title} />
                    ) : (
                        list.map((item, idx) => renderCard(item, columnId, idx))
                    )}
                    {hasMore && (
                        <LoadMoreCard type="button" onClick={onLoadMore}>
                            Load More
                        </LoadMoreCard>
                    )}
                </>
            )}
        </KanbanColumnBody>
    );

    return (
        <Container className="sprint-kanban-root" $display="flex" $flexDirection="column" $flex="1" $minHeight="0" $overflow="hidden">
            <SprintKanbanDragCursorStyle />
            <SprintKanbanScrollChainStyle />
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                <SprintKanbanFill className="sprint-kanban-fill-natural">
                    <LayoutGroup id="client-tickets-kanban-board">
                        {isNarrow ? (
                            <KanbanThreeColumnRow className="sprint-kanban-column-row-natural" style={{ marginTop: 0 }}>
                                <DroppableKanbanColumnPanel columnId={CLIENT_TICKET_COL.ACTIVE}>
                                    <KanbanColumnHeader>
                                        <KanbanColumnTitle>Active</KanbanColumnTitle>
                                        <KanbanColumnCountBadge $variant={COL_VARIANT.ACTIVE}>{activeRows.length}</KanbanColumnCountBadge>
                                    </KanbanColumnHeader>
                                    {renderColumnBody(CLIENT_TICKET_COL.ACTIVE, activeRows, 'Active')}
                                </DroppableKanbanColumnPanel>
                                <DroppableKanbanColumnPanel columnId={CLIENT_TICKET_COL.SUBMITTED}>
                                    <KanbanColumnHeader>
                                        <KanbanColumnTitle>Submitted</KanbanColumnTitle>
                                        <KanbanColumnCountBadge $variant={COL_VARIANT.SUBMITTED}>{submittedRows.length}</KanbanColumnCountBadge>
                                    </KanbanColumnHeader>
                                    {renderColumnBody(CLIENT_TICKET_COL.SUBMITTED, submittedRows, 'Submitted')}
                                </DroppableKanbanColumnPanel>
                                <DroppableKanbanColumnPanel columnId={CLIENT_TICKET_COL.ACKNOWLEDGED}>
                                    <KanbanColumnHeader>
                                        <KanbanColumnTitle>Acknowledged</KanbanColumnTitle>
                                        <TaskCardKanbanStatusPillCount as="span">{acknowledgedRows.length}</TaskCardKanbanStatusPillCount>
                                    </KanbanColumnHeader>
                                    {renderColumnBody(CLIENT_TICKET_COL.ACKNOWLEDGED, acknowledgedRows, 'Acknowledged')}
                                </DroppableKanbanColumnPanel>
                            </KanbanThreeColumnRow>
                        ) : (
                            <div className="sprint-kanban-desktop-split">
                                <div data-sprint-kanban-sticky-strip className="sprint-kanban-sticky-strip">
                                    <div ref={headerScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onHeaderScroll}>
                                        <div style={kanbanDesktopBoardHeaderRowInnerStyle}>
                                            <div style={kanbanDesktopHeaderCellFlexStyle}>
                                                <KanbanColumnHeader>
                                                    <KanbanColumnTitle>Active</KanbanColumnTitle>
                                                    <KanbanColumnCountBadge $variant={COL_VARIANT.ACTIVE}>{activeRows.length}</KanbanColumnCountBadge>
                                                </KanbanColumnHeader>
                                            </div>
                                            <div style={kanbanDesktopHeaderCellFlexStyle}>
                                                <KanbanColumnHeader>
                                                    <KanbanColumnTitle>Submitted</KanbanColumnTitle>
                                                    <KanbanColumnCountBadge $variant={COL_VARIANT.SUBMITTED}>{submittedRows.length}</KanbanColumnCountBadge>
                                                </KanbanColumnHeader>
                                            </div>
                                            <div style={kanbanDesktopHeaderCellFlexStyle}>
                                                <KanbanColumnHeader>
                                                    <KanbanColumnTitle>Acknowledged</KanbanColumnTitle>
                                                    <TaskCardKanbanStatusPillCount as="span">{acknowledgedRows.length}</TaskCardKanbanStatusPillCount>
                                                </KanbanColumnHeader>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="sprint-kanban-desktop-body-wrap">
                                    <div ref={bodyScrollRef} className="sprint-kanban-h-scroll-sync" onScroll={onBodyScroll}>
                                        <div className="sprint-kanban-desktop-board-row" style={kanbanDesktopBoardRowInnerStyle}>
                                            <DroppableKanbanColumnPanel columnId={CLIENT_TICKET_COL.ACTIVE}>
                                                {renderColumnBody(CLIENT_TICKET_COL.ACTIVE, activeRows, 'Active')}
                                            </DroppableKanbanColumnPanel>
                                            <DroppableKanbanColumnPanel columnId={CLIENT_TICKET_COL.SUBMITTED}>
                                                {renderColumnBody(CLIENT_TICKET_COL.SUBMITTED, submittedRows, 'Submitted')}
                                            </DroppableKanbanColumnPanel>
                                            <DroppableKanbanColumnPanel columnId={CLIENT_TICKET_COL.ACKNOWLEDGED}>
                                                {renderColumnBody(CLIENT_TICKET_COL.ACKNOWLEDGED, acknowledgedRows, 'Acknowledged')}
                                            </DroppableKanbanColumnPanel>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </LayoutGroup>
                </SprintKanbanFill>
                <DragOverlay>
                    {activeDragPayload?.id ? (
                        <div style={{ width: 420, maxWidth: '92vw' }}>
                            <ClientTicketCard
                                item={
                                    [...(activeRows ?? []), ...(submittedRows ?? []), ...(acknowledgedRows ?? [])].find(
                                        (x) => String(getClientTicketApiId(x) ?? '') === String(activeDragPayload.id),
                                    ) ?? null
                                }
                            />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Container>
    );
}
